import Vue from 'vue';
import M from 'materialize-css';
import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';
import axios from 'axios';

import App from './App.vue';
import router from './router';
import store from './store';

// Import the plugin here
import Auth0Plugin from './plugins/auth0';
import AxiosPlugin from './plugins/axios';

Vue.config.productionTip = true;
Vue.config.performance = true;

Sentry.init({
  dsn: window.env.VUE_APP_SENTRY_DSN,
  integrations: [
    new Integrations.Vue({
      Vue,
      attachProps: true,
      logErrors: true,
    }),
  ],
  environment: window.env.ENVIRONMENT,
  debug: true,
  maxValueLength: 1000,
  beforeSend(event) {
    console.warn(event);
    return event;
  },
  whitelistUrls: [],
  ignoreErrors: [],
});

Vue.use(AxiosPlugin);
// Install the authentication plugin here
Vue.use(Auth0Plugin, {
  domain: window.env.AUTH0_DOMAIN,
  clientId: window.env.AUTH0_CLIENTID,
  onRedirectCallback: appState => {
    router.push(
      appState && appState.targetUrl
        ? appState.targetUrl
        : window.location.pathname,
    );
  },
});

/* Mixins */

Vue.mixin({
  data() {
    return {
      materialize: M,
    };
  },
  methods: {
    toast(msg='Testing...', color='grey') {
      M.toast({
        html: msg,
        classes: color,
      });
    },
    toastWithLink(msg, linkText, linkHref, color='green', duration=8000) {
      M.toast({
        html: `${msg} <a href="${linkHref}" class="toast-action-link">${linkText}</a>`,
        classes: color,
        displayLength: duration,
      });
    },
    clip(text, showToast=true) {
      const elem = document.createElement('textarea');
      elem.value = text;
      elem.setAttribute('readonly', '');
      elem.style.position = 'absolute';
      elem.style.left = '-9999px';
      document.body.appendChild(elem);
      elem.select();
      document.execCommand('copy');
      document.body.removeChild(elem);
      if (showToast) {
        this.toast('Copied to clipboard!', 'green lighten-1');
      }
    },
    randomChoice(start = 0, end = 255) {
      let res = Math.floor((Math.random() * end) + 1);
      while (res < start) {
        res = Math.floor((Math.random() * end) + 1);
      }
      return res;
    },
    sum(arr) {
      return arr.reduce((a, b) => a + b, 0);
    },
    async ping(target, msg) {
      const { data } = await axios.post(`${store.getters.VUE_APP_APING_URL}/insights/common/slack/`, {
        channel: target,
        message: msg,
      });
      return data;
    },
    echo(msg) {
      console.debug(msg);
    },
    sortArray(arr, dotProps, reversed=false) {
      const props = dotProps.split('.');
      arr.sort((a, b) => {
        let valueA = a;
        let valueB = b;
        props.forEach(prop => {
          valueA = valueA[prop];
          valueB = valueB[prop];
        });
        if (valueA > valueB) { return 1; }
        if (valueA < valueB) { return -1; }
        return 0;
      });
      if (reversed) { arr.reverse(); }
      return arr;
    },
    randomColor() {
      const colors = [
        'red', 'pink', 'purple', 'deep-purple',
        'indigo', 'blue', 'light-blue', 'cyan',
        'teal', 'green', 'light-green', 'lime',
        'yellow', 'amber', 'orange', 'deep-orange',
      ];
      return colors[this.randomChoice(0, colors.length - 1)];
    },
    chunkArray(arr, size) { // https://medium.com/@Dragonza/four-ways-to-chunk-an-array-e19c889eac4
      if (!arr) return [];
      const firstChunk = arr.slice(0, size);
      if (!firstChunk.length) {
        return arr;
      }
      return [firstChunk].concat(this.chunkArray(arr.slice(size, arr.length), size));
    },
  },
});

/* Filters */

Vue.filter('pretty', name => {
  if (!name) return '';
  const punctuations = Array.from('!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~“”’');
  const letters = Array.from('abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890 ');
  name = name.replace('.com', '');
  name = Array.from(name).filter(char => letters.includes(char)).join('');
  name = name
    .replace(/^THE /, '')
    .replace(/^The /, '')
    .replace(/^the/, '')
    .replace(/ INC$/, '')
    .replace(/ Inc$/, '')
    .replace(/ inc$/, '')
    .replace(/ CORP$/, '')
    .replace(/ Corp$/, '')
    .replace(/ corp$/, '')
    .replace(/ INCORPORATED$/, '')
    .replace(/ Incorporated$/, '')
    .replace(/ incorporated$/, '')
    .replace(/ CORPORATION$/, '')
    .replace(/ Corporation$/, '')
    .replace(/ corporation$/, '')
    .replace(/ COMPANY$/, '')
    .replace(/ Company$/, '')
    .replace(/ company$/, '')
    .replace(/ COMPANY$/, '')
    .replace(/ Company$/, '')
    .replace(/ company$/, '')
    .replace(/ AND$/, '')
    .replace(/ And$/, '')
    .replace(/ and$/, '')
  ;
  return name.trim();
});
Vue.filter('datetime', dateString => new Date(dateString).toLocaleString());
Vue.filter('date', dateString => new Date(dateString).toLocaleDateString());
Vue.filter('round', (number, decimals=2) => (number ? Number(number.toFixed(decimals)) : ''));
Vue.filter('thousands', number => number.toLocaleString());
Vue.filter('ago', mSecs => {
  const secs = mSecs / 1000.0;
  const mins = secs / 60.0;
  const hours = mins / 60.0;
  const days = hours / 24.0;
  if (days >= 1.0) {
    return `${days.toFixed(0)} Days Ago`;
  }
  if (hours >= 1.0) {
    return `${hours.toFixed(0)} Hours Ago`;
  }
  if (mins >= 1.0) {
    return `${mins.toFixed(0)} Mins Ago`;
  }
  return `${secs.toFixed(0)} Secs Ago`;
});
Vue.filter('titlecase', str => str
  .replace(/-/g, ' ')
  .replace(/_/g, ' ')
  .split(' ')
  .map(piece => `${piece.slice(0, 1).toUpperCase()}${piece.slice(1, piece.length)}`)
  .join(' '));
Vue.filter('marketSize', marketCap => {
  if (marketCap > 20000000000) {
    return 'Large';
  }
  if (marketCap > 2000000000) {
    return 'Mid';
  }
  if (marketCap > 0) {
    return 'Small';
  }
  return '';
});
Vue.filter('currency', (number, currencyCode='USD') => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: currencyCode,
}).format(number));

const vm = new Vue({
  router,
  store,
  render: h => h(App),
}).$mount('#app');

if (window.env.ENVIRONMENT === 'development') {
  window.vm = vm;
  Vue.config.devtools = true;
}
